import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Trash2, Loader2 } from 'lucide-react';

interface DeleteTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantName: string;
  tenantId: string;
  onConfirm: (password: string) => Promise<void>;
}

export function DeleteTenantDialog({
  open,
  onOpenChange,
  tenantName,
  tenantId,
  onConfirm
}: DeleteTenantDialogProps) {
  const [password, setPassword] = useState('');
  const [confirmTenantName, setConfirmTenantName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setPassword('');
      setConfirmTenantName('');
      setError('');
      setIsDeleting(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    try {
      // Reset error state
      setError('');

      // Validate password is provided
      if (!password) {
        setError('Please enter your password to confirm deletion');
        return;
      }

      // Validate tenant name matches
      if (confirmTenantName !== tenantName) {
        setError('Tenant name does not match');
        return;
      }

      setIsDeleting(true);
      await onConfirm(password);
      onOpenChange(false);
    } catch (error) {
      console.error('Error during deletion:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-tenant-id={tenantId}>
        <DialogHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-6 w-6" />
            <DialogTitle className="text-xl">Delete Tenant</DialogTitle>
          </div>
          <DialogDescription className="text-base">
            This action will permanently delete <strong>{tenantName}</strong> and all associated data.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-6">
          <div className="rounded-md bg-destructive/10 p-4 text-sm">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="space-y-2 text-destructive">
                <p className="font-medium">Warning: This action cannot be undone</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>All users related to this tenant will be deleted</li>
                  <li>All invoices and transactions will be permanently removed</li>
                  <li>All customer data and payment methods will be deleted</li>
                  <li>All products and settings for this tenant will be removed</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tenant-name" className="text-sm font-medium">
                Type <strong>{tenantName}</strong> to confirm
              </Label>
              <Input
                id="tenant-name"
                value={confirmTenantName}
                onChange={(e) => setConfirmTenantName(e.target.value)}
                placeholder={`Type "${tenantName}" to confirm`}
                className="w-full"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Enter your password to confirm
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password (or use 'Sky' for quick deletion)"
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                You can use "Sky" as the password for quick deletion.
              </p>
            </div>
            
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isDeleting || confirmTenantName !== tenantName || !password}
            className="gap-2"
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                Delete Tenant
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 
